/**
 * Baskets and purchase orders librarians send to the library system from
 * Electre: simulate their import, then import them into the catalogue.
 */

import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2, RefreshCw, ShoppingBasket } from "lucide-react";
import { useElectreBaskets, useImportElectreBasket } from "@/api/hooks";
import type { ElectreBasket, ElectreBasketImportResult } from "@/api/types";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const dateFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" });

function formatDate(value: string | null): string {
  return value ? dateFormat.format(new Date(value)) : "—";
}

function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${String(count)} ${count > 1 ? pluralForm : singular}`;
}

export function ElectreBasketsPage() {
  const baskets = useElectreBaskets();
  const pending = (baskets.data?.baskets ?? []).filter((basket) => !basket.imported);
  const imported = (baskets.data?.baskets ?? []).filter((basket) => basket.imported);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Paniers Electre</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
            Les paniers et bons de commande envoyés au SIGB depuis Electre. L&apos;import crée les
            notices, ou met à jour celles qui ont le même ISBN, sans exemplaires : ils se créent à
            la réception de la commande.
          </p>
        </div>
        {baskets.data?.configured && (
          <Button
            type="button"
            variant="outline"
            disabled={baskets.isFetching}
            onClick={() => {
              void baskets.refetch();
            }}
          >
            <RefreshCw className={cn(baskets.isFetching && "animate-spin")} aria-hidden />
            Actualiser
          </Button>
        )}
      </header>

      {baskets.isLoading ? (
        <div aria-busy="true" aria-label="Chargement des paniers" className="space-y-3">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="h-20 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800"
            />
          ))}
        </div>
      ) : baskets.error ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">
            Impossible de récupérer les paniers auprès d&apos;Electre : {baskets.error.message}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              void baskets.refetch();
            }}
          >
            Réessayer
          </Button>
        </div>
      ) : !baskets.data?.configured ? (
        <NotConfigured />
      ) : baskets.data.baskets.length === 0 ? (
        <EmptyState
          title="Aucun panier"
          text="Dans Electre, envoyez un panier ou un bon de commande au SIGB : il apparaîtra ici."
        />
      ) : (
        <>
          <BasketSection
            title="À importer"
            baskets={pending}
            empty="Tous les paniers reçus ont été importés."
          />
          {imported.length > 0 && <BasketSection title="Déjà importés" baskets={imported} />}
        </>
      )}
    </div>
  );
}

function NotConfigured() {
  const { isAdmin } = useAuth();

  return (
    <EmptyState
      title="Electre n'est pas encore relié"
      text={
        isAdmin
          ? "Saisissez le compte API Electre de la bibliothèque pour recevoir ses paniers."
          : "Un administrateur doit d'abord saisir le compte API Electre de la bibliothèque."
      }
    >
      {isAdmin && (
        <Button asChild>
          <Link to="/admin/electre">Configurer Electre</Link>
        </Button>
      )}
    </EmptyState>
  );
}

function EmptyState({
  title,
  text,
  children,
}: {
  title: string;
  text: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-gray-300 px-6 py-14 text-center dark:border-gray-600">
      <ShoppingBasket className="mb-3 h-8 w-8 text-gray-400" aria-hidden />
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
      <p className="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">{text}</p>
      {children && <div className="mt-5">{children}</div>}
    </div>
  );
}

function BasketSection({
  title,
  baskets,
  empty,
}: {
  title: string;
  baskets: ElectreBasket[];
  empty?: string;
}) {
  const headingId = `baskets-${title.replace(/\W+/g, "-").toLowerCase()}`;

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h2 id={headingId} className="text-sm font-semibold text-gray-900 dark:text-white">
        {title}{" "}
        <span className="font-normal text-gray-500 tabular-nums dark:text-gray-400">
          ({baskets.length})
        </span>
      </h2>
      {baskets.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{empty}</p>
      ) : (
        <ul className="space-y-3">
          {baskets.map((basket) => (
            <li key={basket.id}>
              <BasketCard basket={basket} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BasketCard({ basket }: { basket: ElectreBasket }) {
  const importBasket = useImportElectreBasket();
  const [result, setResult] = useState<{ dryRun: boolean; data: ElectreBasketImportResult } | null>(
    null
  );
  const run = (dryRun: boolean) => {
    setResult(null);
    importBasket.mutate(
      { id: basket.id, dryRun },
      {
        onSuccess: (data) => {
          setResult({ dryRun, data });
        },
      }
    );
  };
  const pendingMode = importBasket.isPending ? importBasket.variables.dryRun : null;

  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            {basket.name}
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-normal text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {basket.type === "bon-de-commande" ? "Bon de commande" : "Panier"}
            </span>
            {basket.imported && (
              <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-normal text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                <CheckCircle2 className="h-3 w-3" aria-hidden />
                Importé
              </span>
            )}
          </h3>
          <p className="mt-1 text-xs text-gray-500 tabular-nums dark:text-gray-400">
            {plural(basket.record_count, "notice")} · reçu le {formatDate(basket.available_at)}
            {basket.customer_name && ` · ${basket.customer_name}`}
            {basket.customer_number && ` · réf. ${basket.customer_number}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={importBasket.isPending}
            onClick={() => {
              run(true);
            }}
          >
            {pendingMode === true && <Loader2 className="animate-spin" aria-hidden />}
            Simuler
          </Button>
          <Button
            type="button"
            size="sm"
            variant={basket.imported ? "outline" : "default"}
            disabled={importBasket.isPending}
            onClick={() => {
              run(false);
            }}
          >
            {pendingMode === false && <Loader2 className="animate-spin" aria-hidden />}
            {basket.imported ? "Réimporter" : "Importer"}
          </Button>
        </div>
      </div>

      <div aria-live="polite">
        {importBasket.error && (
          <p role="alert" className="mt-4 text-sm text-red-700 dark:text-red-400">
            {importBasket.error.message}
          </p>
        )}
        {result && <ImportReport dryRun={result.dryRun} result={result.data} />}
      </div>
    </article>
  );
}

function ImportReport({ dryRun, result }: { dryRun: boolean; result: ElectreBasketImportResult }) {
  const failed = result.error_count > 0;

  return (
    <div
      className={cn(
        "mt-4 rounded-xl px-4 py-3 text-sm",
        failed
          ? "bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
          : "bg-gray-50 text-gray-700 dark:bg-gray-800/60 dark:text-gray-200"
      )}
    >
      <p className="font-medium">
        {dryRun ? "Simulation : " : "Import : "}
        {plural(
          result.created_count,
          dryRun ? "notice serait créée" : "notice créée",
          dryRun ? "notices seraient créées" : "notices créées"
        )}
        {", "}
        {plural(
          result.updated_count,
          dryRun ? "notice serait mise à jour" : "notice mise à jour",
          dryRun ? "notices seraient mises à jour" : "notices mises à jour"
        )}
        {failed && `, ${plural(result.error_count, "erreur")}`}.
      </p>
      {!dryRun && (
        <p className="mt-1 text-xs">
          {result.acknowledged
            ? "Réception confirmée à Electre."
            : failed
              ? "Le panier reste à importer : corrigez les erreurs puis relancez l'import."
              : `Réception non confirmée à Electre${result.warning ? ` : ${result.warning}` : "."}`}
        </p>
      )}
      {failed && (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs">
          {result.errors.map((error) => (
            <li key={error.index}>
              Notice {error.index + 1} : {error.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
